import type { ContactFormProps, FormTypePreset } from './contact-form';
import type { JobsiteBusinessContext, JobsiteBusinessServiceValue } from '../../types/runtime-config';
import { resolveCtaOfferLine, resolvesToPhoneUrl } from "../../lib/cta-defaults";
import type { ComponentConfig } from '@puckeditor/core';
import { ContactForm } from './contact-form';
import { ContactFormFields } from "./contact-form.fields";
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { formatPhone } from "@servicepro/utils";
import { getWindowJobsiteConfig } from '../../types/runtime-config';
import { resolveBusinessCtaText } from "../../lib/business-bound-prop";
import { serviceAreaSentenceList } from "../../lib/business-facts";

// Default headings based on form type
const getDefaultHeading = (formType: FormTypePreset, businessName?: string): string => {
    switch (formType) {
      case "quick-quote": {
        return "Get a Free Quote";
      }
      case "schedule": {
        return "Schedule Your Service";
      }
      case "emergency": {
        return "Need Emergency Service?";
      }
      default: {
        if (businessName) {
          return `Contact ${businessName}`;
        }
        return "Get In Touch";
      }
    }
  },
  getDefaultSubheading = (formType: FormTypePreset, businessPhone?: string): string => {
    switch (formType) {
      case "quick-quote": {
        return "Tell us about your project and we'll get back to you with a free estimate.";
      }
      case "schedule": {
        return "Choose a time that works for you and we'll confirm your appointment.";
      }
      case "emergency": {
        if (businessPhone) {
          return `Call ${formatPhone(businessPhone)} for immediate assistance or submit below.`;
        }
        return "We respond to emergencies 24/7. Submit your request now.";
      }
      default: {
        if (businessPhone) {
          return `Call us at ${formatPhone(businessPhone)} or fill out the form below`;
        }
        return "Fill out the form below and we'll get back to you as soon as possible";
      }
    }
  },
  ContactFormConfig: ComponentConfig<ContactFormPuckProps> = {
    defaultProps: {
      formType: "contact",
      heading: "", // Let render handle the default based on formType
      layout: "centered",
      showUrgencySelector: "no",
      subheading: "", // Let render handle the default based on formType
      submitButtonText: "", // Let component use formType default
      successMessage: "Thank you! We'll be in touch soon.",
      variant: "card",
    },
    fields: {
      formType: ContactFormFields.formType,
      heading: {
        ...ContactFormFields.heading,
        contentEditable: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: ({ value, onChange, field }: any) => (
          <RegenerableTextField
            value={value || ""}
            onChange={onChange}
            namespace="home.contactform.heading"
            label="Heading"
            multiline={false}
            fieldMeta={field?.metadata}
          />
        ),
      },
      subheading: {
        ...ContactFormFields.subheading,
        contentEditable: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: ({ value, onChange, field }: any) => (
          <RegenerableTextField
            value={value || ""}
            onChange={onChange}
            namespace="home.contactform.subheading"
            label="Subheading"
            multiline={true}
            fieldMeta={field?.metadata}
          />
        ),
      },
      variant: ContactFormFields.variant,
      showUrgencySelector: ContactFormFields.showUrgencySelector,
      layout: ContactFormFields.layout,
      submitButtonText: {
        ...ContactFormFields.submitButtonText,
        contentEditable: true,
      },
      successMessage: ContactFormFields.successMessage,
      backgroundColor: ContactFormFields.backgroundColor,
    },
    label: "Contact Form",
    render: ({ id, puck, ...props }) => {
      // Access business data from Puck metadata OR window config (fallback)
      const puckMetadata = puck?.metadata as { business?: JobsiteBusinessContext } | undefined,
        windowConfig = getWindowJobsiteConfig(),
        business = puckMetadata?.business || windowConfig?.business,

        // Get phone number - check both 'phone' and 'telephone' property names
        businessPhone = business?.phone || business?.telephone,
        businessUrl = business?.website || business?.url || windowConfig?.siteUrl,

        // Get services from business context for dropdown
        serviceOptions =
          business?.services?.map((service: JobsiteBusinessServiceValue) => {
            if (typeof service === "string") {
              return service;
            }
            return service.name || "";
          },
          ) || [],

        // Resolve form type (default to contact)
        formType = props.formType || "contact",

        // Determine hours available string (24/7 for emergency services)
        hoursAvailable = business?.is24Hours === true || formType === "emergency" ? "24/7" : undefined,

        // Submit button: owner Puck edit (props.submitButtonText) wins; otherwise
        // When the business has a Site-level CTA, use it. When neither, leave
        // Undefined so the formType-based preset default kicks in
        // (`getDefaultSubmitText` in contact-form.tsx). Tolerate a ReactNode:
        // The editors swap the string for an inline-editable element (the field
        // Is contentEditable), and an element means the owner is editing it, so
        // It wins like any owner-set value. Strings keep the exact old semantics.
        submitButtonText =
          typeof props.submitButtonText === "string" || props.submitButtonText == null
            ? props.submitButtonText?.trim()
              ? props.submitButtonText
              : // A phone-posture site CTA ("Call Now") is a lie on a submit
                // Button — pressing it submits a form. Only borrow the site CTA
                // When it describes a form action; else the formType default wins.
                business?.ctaPrimaryLabel?.trim() &&
                  !resolvesToPhoneUrl(resolveBusinessCtaText(undefined, business))
                ? resolveBusinessCtaText(undefined, business)
                : undefined
            : props.submitButtonText,

        // Default values if not provided in props
        resolvedProps = {
          ...props,
          formType,
          heading: props.heading || getDefaultHeading(formType, business?.name),
          subheading: props.subheading || getDefaultSubheading(formType, businessPhone),
          submitButtonText,
          variant: props.variant || "card",
          serviceOptions,
          // Schema props from business context
          businessName: business?.name,
          businessPhone,
          businessUrl,
          hasTrackingPhone: Boolean((business)?.hasTrackingPhone),
          hoursAvailable,
          // Same canonical slug the booking widget uses — photo uploads resolve on it.
          businessSlug: business?.slug,
          // Read live, so the towns and offer the owner sets show on the next publish.
          offerLine: resolveCtaOfferLine(business),
          serviceAreaList: serviceAreaSentenceList(business),
        };

      return <ContactForm id={id} {...resolvedProps} />;
    },
  };

// The builder-only prop (preview) stays out of the Puck generic: Puck's
// Slot-type transform mangles nested Data-shaped props (puck#1519), and a
// Jobsite page never sets it — only the form builder and stories do.
type ContactFormPuckProps = Omit<ContactFormProps, "preview">;

export { ContactFormConfig };
